<script setup lang="ts">
/**
 * La courbe d'une alarme (`courbeSVG`, S:7057-7190), en SVG, sans bibliothèque
 * de graphique (plan §2.7, point 4).
 *
 * Ce que l'utilisateur doit vivre : il voit la mesure autour du dépassement,
 * le seuil, depuis quand l'alarme est active, et chaque événement numéroté
 * comme dans le journal. La courbe ne ment pas : un trou de mesure se voit
 * comme un trou hachuré « pas de mesure », jamais comme une ligne ni comme un
 * zéro (`utils/segments.ts`). Le même contenu se lit en toutes lettres sous
 * la courbe et par le lecteur d'écran (`role="img"`).
 */
// ================================
// IMPORTS
// ================================
import { computed, useId } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type {
    AlarmesV2Bilan,
    AlarmesV2SeuilsFiges,
} from "@modules/alarmes-v2/types/alarmes-v2"
import type { LigneLue } from "@modules/alarmes-v2/utils/journal"
import {
    coteFranchi,
    decouperLaCourbe,
    resumerLaCourbe,
    type CoteDuSeuil,
    type PointLu,
} from "@modules/alarmes-v2/utils/segments"
import {
    formaterInstant,
    formaterMesure,
} from "@modules/alarmes-v2/utils/mesure"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// TYPES & INTERFACES
// ================================
/** Une bande d'état de fond (S:7104-7126). */
interface Bande {
    sorte: "depassement" | "active" | "normale"
    a: number
    b: number
}

/** Un marqueur d'événement, placé sur le rail au-dessus de la courbe. */
interface Marqueur {
    num: number
    x: number
    xRail: number
    libelle: string
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Le bilan de l'alarme : `points_recents`, `attendu_entre_points_s`, l'extrême. */
        bilan?: AlarmesV2Bilan | null
        /** Les seuils figés à l'ouverture. */
        seuils?: AlarmesV2SeuilsFiges | null
        /** Le motif d'ouverture : une sonde muette prolonge le trou jusqu'au dernier événement. */
        motifOuverture?: string | null
        /** Les lignes du journal, numérotées : leurs marqueurs. */
        evenements?: LigneLue[]
    }>(),
    { bilan: null, seuils: null, motifOuverture: null, evenements: () => [] }
)

/** La géométrie (S:7070). */
const LARGEUR = 640
const HAUTEUR = 230
const PAD_G = 46
const PAD_D = 12
const PAD_B = 24

/** Un identifiant propre à cette courbe, pour son motif de hachures. */
const ID_HACHURES = `hachures-${useId()}`

// ================================
// COMPUTED
// ================================
/** Les événements datés, les seuls qui se placent sur la courbe. */
const getEvenements = computed(() =>
    props.evenements.filter(
        (e): e is LigneLue & { ms: number } => e.ms !== null
    )
)

const getAvecMarqueurs = computed(() => getEvenements.value.length > 0)
const getPadHaut = computed(() => (getAvecMarqueurs.value ? 36 : 12))

/** Le dernier instant de la fenêtre : la dernière mesure ou le dernier événement. */
const getJusqua = computed((): number | null => {
    const ms = getEvenements.value.map((e) => e.ms)
    return ms.length ? Math.max(...ms) : null
})

/** Le découpage : segments, trous, points écartés. */
const getDecoupage = computed(() =>
    decouperLaCourbe(
        props.bilan?.points_recents,
        props.bilan?.attendu_entre_points_s,
        props.motifOuverture === "silence_sonde" ? getJusqua.value : null
    )
)

const getPoints = computed((): PointLu[] => getDecoupage.value.segments.flat())

const getUnite = computed(() => props.seuils?.unite ?? null)

/** Les bornes servies, chacune une ligne de seuil. */
const getBornes = computed(() => {
    const bornes: { cote: "haut" | "bas"; v: number }[] = []
    const haut = props.seuils?.borne_haute
    const bas = props.seuils?.borne_basse
    if (typeof haut === "number" && Number.isFinite(haut))
        bornes.push({ cote: "haut", v: haut })
    if (typeof bas === "number" && Number.isFinite(bas))
        bornes.push({ cote: "bas", v: bas })
    return bornes
})

/** Le côté réellement franchi : jamais supposé quand deux bornes sont servies. */
const getCote = computed(
    (): CoteDuSeuil =>
        coteFranchi(
            {
                haute: props.seuils?.borne_haute,
                basse: props.seuils?.borne_basse,
            },
            props.bilan?.valeur_extreme,
            getPoints.value
        )
)

/** Les bornes du temps. */
const getX = computed(() => {
    const ms = [
        ...getPoints.value.map((p) => p.ms),
        ...getEvenements.value.map((e) => e.ms),
    ]
    const min = Math.min(...ms)
    const max = Math.max(...ms)
    return { min, max: max === min ? min + 1 : max }
})

/** Les bornes des valeurs, seuils compris, avec une marge (S:7075-7079). */
const getV = computed(() => {
    const vals = [
        ...getPoints.value.map((p) => p.v),
        ...getBornes.value.map((b) => b.v),
    ]
    let min = Math.min(...vals)
    let max = Math.max(...vals)
    const marge = (max - min) * 0.18 || 1
    min -= marge
    max += marge
    return { min, max }
})

/** Abscisse d'un instant. */
function X(ms: number): number {
    const { min, max } = getX.value
    return PAD_G + ((ms - min) / (max - min)) * (LARGEUR - PAD_G - PAD_D)
}

/** Ordonnée d'une valeur. */
function Y(v: number): number {
    const { min, max } = getV.value
    return (
        getPadHaut.value +
        ((max - v) / (max - min)) * (HAUTEUR - getPadHaut.value - PAD_B)
    )
}

const getHautZone = computed(() => HAUTEUR - getPadHaut.value - PAD_B)

/** Vrai pour une valeur du côté hors seuil. */
function horsSeuil(v: number): boolean {
    return getBornes.value.some((b) => (b.cote === "haut" ? v > b.v : v < b.v))
}

/** Le premier instant d'un événement d'une sorte. */
function instantDe(sorte: LigneLue["sorte"]): number | null {
    return getEvenements.value.find((e) => e.sorte === sorte)?.ms ?? null
}

/** Les bandes d'état : dépassement, alarme active, normale (S:7104-7126). */
const getBandes = computed((): Bande[] => {
    const ouverture = instantDe("ouverture")
    if (ouverture === null || getBornes.value.length === 0) return []
    const normale = instantDe("normale")
    const fin = getX.value.max
    const bandes: Bande[] = []
    const debut = getPoints.value.find(
        (p) => p.ms < ouverture && horsSeuil(p.v)
    )?.ms
    if (debut !== undefined && debut < ouverture)
        bandes.push({ sorte: "depassement", a: debut, b: ouverture })
    bandes.push({ sorte: "active", a: ouverture, b: normale ?? fin })
    if (normale !== null && normale < fin)
        bandes.push({ sorte: "normale", a: normale, b: fin })
    return bandes
})

/** Le motif de remplissage de chaque bande. */
const CLASSE_BANDE: Record<Bande["sorte"], string> = {
    depassement: "fill-amber-100",
    active: "fill-red-100",
    normale: "fill-green-100",
}

/** Le libellé d'une bande, s'il tient. */
function libelleBande(sorte: Bande["sorte"]): string {
    return {
        depassement: t("alarmesV2.courbe.bandeDepassement"),
        active: t("alarmesV2.courbe.bandeActive"),
        normale: t("alarmesV2.courbe.bandeNormale"),
    }[sorte]
}

/** Les segments en `points` de polyligne, ou un point seul. */
const getTraces = computed(() =>
    getDecoupage.value.segments.map((seg) =>
        seg.length === 1
            ? { seul: true, cx: X(seg[0].ms), cy: Y(seg[0].v), d: "" }
            : {
                  seul: false,
                  cx: 0,
                  cy: 0,
                  d: seg.map((p) => `${X(p.ms)},${Y(p.v)}`).join(" "),
              }
    )
)

/** La dernière mesure, en point plein. */
const getDernier = computed(() => {
    const pts = getPoints.value
    if (pts.length === 0) return null
    const p = pts.reduce((x, y) => (y.ms > x.ms ? y : x))
    return { cx: X(p.ms), cy: Y(p.v) }
})

/** Les marqueurs, écartés d'un cran quand deux tombent au même endroit (S:7160-7178). */
const getMarqueurs = computed((): Marqueur[] => {
    const parX: Record<number, number> = {}
    return getEvenements.value.map((e) => {
        const x = X(e.ms)
        const cle = Math.round(x / 6)
        parX[cle] = (parX[cle] ?? 0) + 1
        const rang = parX[cle] - 1
        const xRail = Math.min(
            LARGEUR - PAD_D - 8,
            Math.max(PAD_G + 8, x + rang * 18)
        )
        return {
            num: e.num,
            x,
            xRail,
            libelle: `${e.num}. ${e.quand}, ${e.titre}`,
        }
    })
})

/** Un nombre de l'axe, dans l'unité de l'alarme. */
function valeurAxe(v: number): string {
    return formaterMesure(v, getUnite.value, tr)
}

/** Un instant de l'axe. */
function instantAxe(ms: number): string {
    return formaterInstant(new Date(ms).toISOString(), tr) ?? ""
}

/** Le résumé en toutes lettres (S:7192-7214). */
const getResume = computed((): string =>
    resumerLaCourbe(getDecoupage.value, {
        t: tr,
        unite: getUnite.value,
        cote: getCote.value,
        extreme: props.bilan?.valeur_extreme,
        extremeLe: props.bilan?.valeur_extreme_le,
        dureeHorsSeuilS: props.bilan?.duree_hors_seuil_s,
    })
)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <figure class="flex flex-col gap-2" data-test="courbe">
        <p
            v-if="getPoints.length === 0"
            class="text-sm text-surface-600"
            data-test="courbe-vide"
        >
            {{ t("alarmesV2.courbe.aucunPoint") }}
        </p>
        <svg
            v-else
            :viewBox="`0 0 ${LARGEUR} ${HAUTEUR}`"
            role="img"
            :aria-label="getResume"
            class="block h-auto w-full"
            data-test="courbe-svg"
        >
            <defs>
                <pattern
                    :id="ID_HACHURES"
                    width="6"
                    height="6"
                    patternUnits="userSpaceOnUse"
                    patternTransform="rotate(45)"
                >
                    <line
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="6"
                        class="stroke-surface-400"
                        stroke-width="2"
                    />
                </pattern>
            </defs>

            <!-- Les bandes d'état -->
            <g v-for="b in getBandes" :key="b.sorte" data-test="bande">
                <rect
                    :x="X(b.a)"
                    :y="getPadHaut"
                    :width="Math.max(0, X(b.b) - X(b.a))"
                    :height="getHautZone"
                    :class="CLASSE_BANDE[b.sorte]"
                    opacity="0.6"
                />
                <text
                    v-if="X(b.b) - X(b.a) > 50"
                    :x="(X(b.a) + X(b.b)) / 2"
                    :y="HAUTEUR - PAD_B - 4"
                    font-size="9"
                    text-anchor="middle"
                    class="fill-surface-600"
                >
                    {{ libelleBande(b.sorte) }}
                </text>
            </g>

            <!-- Le cadre -->
            <rect
                :x="PAD_G"
                :y="getPadHaut"
                :width="LARGEUR - PAD_G - PAD_D"
                :height="getHautZone"
                fill="none"
                class="stroke-surface-300"
                stroke-width="1"
            />

            <!-- Le côté hors seuil et le seuil -->
            <g v-for="s in getBornes" :key="s.cote" data-test="seuil">
                <rect
                    :x="PAD_G"
                    :y="s.cote === 'haut' ? getPadHaut : Y(s.v)"
                    :width="LARGEUR - PAD_G - PAD_D"
                    :height="
                        Math.max(
                            0,
                            s.cote === 'haut'
                                ? Y(s.v) - getPadHaut
                                : HAUTEUR - PAD_B - Y(s.v)
                        )
                    "
                    class="fill-red-50"
                    opacity="0.5"
                />
                <line
                    :x1="PAD_G"
                    :y1="Y(s.v)"
                    :x2="LARGEUR - PAD_D"
                    :y2="Y(s.v)"
                    class="stroke-red-600"
                    stroke-width="1.5"
                    stroke-dasharray="5 3"
                />
                <text
                    :x="PAD_G + 4"
                    :y="s.cote === 'bas' ? Y(s.v) + 11 : Y(s.v) - 4"
                    font-size="9"
                    class="fill-red-700"
                >
                    {{
                        t("alarmesV2.courbe.seuil", { valeur: valeurAxe(s.v) })
                    }}
                </text>
            </g>

            <!-- Les trous : pas de mesure -->
            <g
                v-for="(trou, i) in getDecoupage.trous"
                :key="`trou-${i}`"
                data-test="trou"
            >
                <rect
                    :x="X(trou.debut)"
                    :y="getPadHaut"
                    :width="Math.max(0, X(trou.fin) - X(trou.debut))"
                    :height="getHautZone"
                    :fill="`url(#${ID_HACHURES})`"
                    opacity="0.6"
                />
                <text
                    :x="(X(trou.debut) + X(trou.fin)) / 2"
                    :y="getPadHaut + 12"
                    font-size="9"
                    text-anchor="middle"
                    class="fill-surface-600"
                >
                    {{ t("alarmesV2.courbe.pasDeMesure") }}
                </text>
            </g>

            <!-- La mesure, segment par segment, jamais reliée par-dessus un trou -->
            <template v-for="(trace, i) in getTraces" :key="`seg-${i}`">
                <circle
                    v-if="trace.seul"
                    :cx="trace.cx"
                    :cy="trace.cy"
                    r="2.5"
                    class="fill-primary-700"
                    data-test="point-seul"
                />
                <polyline
                    v-else
                    :points="trace.d"
                    fill="none"
                    class="stroke-primary-700"
                    stroke-width="2"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                    data-test="segment"
                />
            </template>
            <circle
                v-if="getDernier"
                :cx="getDernier.cx"
                :cy="getDernier.cy"
                r="4"
                class="fill-primary-800 stroke-surface-0"
                stroke-width="1.5"
            />

            <!-- Les événements, numérotés comme au journal -->
            <g
                v-for="m in getMarqueurs"
                :key="`ev-${m.num}`"
                data-test="marqueur"
            >
                <line
                    :x1="m.x"
                    :y1="getPadHaut - 6"
                    :x2="m.x"
                    :y2="HAUTEUR - PAD_B"
                    class="stroke-surface-400"
                    stroke-width="1"
                    stroke-dasharray="2 3"
                />
                <circle
                    :cx="m.xRail"
                    cy="15"
                    r="8.5"
                    class="fill-surface-0 stroke-primary-700"
                    stroke-width="1.5"
                />
                <text
                    :x="m.xRail"
                    y="18.5"
                    font-size="9.5"
                    font-weight="700"
                    text-anchor="middle"
                    class="fill-primary-700"
                >
                    {{ m.num }}
                </text>
                <title>{{ m.libelle }}</title>
            </g>

            <!-- Les axes -->
            <text
                x="4"
                :y="getPadHaut + 8"
                font-size="9"
                class="fill-surface-600"
            >
                {{ valeurAxe(getV.max) }}
            </text>
            <text
                x="4"
                :y="HAUTEUR - PAD_B"
                font-size="9"
                class="fill-surface-600"
            >
                {{ valeurAxe(getV.min) }}
            </text>
            <text
                :x="PAD_G"
                :y="HAUTEUR - 6"
                font-size="9"
                class="fill-surface-600"
            >
                {{ instantAxe(getX.min) }}
            </text>
            <text
                :x="LARGEUR - PAD_D"
                :y="HAUTEUR - 6"
                font-size="9"
                text-anchor="end"
                class="fill-surface-600"
            >
                {{ instantAxe(getX.max) }}
            </text>
        </svg>

        <!-- ================================ -->
        <!-- LÉGENDE ET RÉSUMÉ -->
        <!-- ================================ -->
        <ul
            v-if="getPoints.length > 0"
            class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-surface-600"
            aria-hidden="true"
        >
            <li v-for="b in getBandes" :key="`leg-${b.sorte}`">
                <span
                    class="mr-1 inline-block h-2.5 w-2.5 align-middle"
                    :class="{
                        'bg-amber-100': b.sorte === 'depassement',
                        'bg-red-100': b.sorte === 'active',
                        'bg-green-100': b.sorte === 'normale',
                    }"
                />{{
                    b.sorte === "depassement"
                        ? t("alarmesV2.courbe.legende.depassement")
                        : b.sorte === "active"
                          ? t("alarmesV2.courbe.legende.active")
                          : t("alarmesV2.courbe.legende.revenue")
                }}
            </li>
            <li>{{ t("alarmesV2.courbe.legende.sansBande") }}</li>
            <li v-if="getBornes.length > 0">
                <span
                    class="mr-1 inline-block h-2.5 w-2.5 bg-red-50 align-middle"
                />{{ t("alarmesV2.courbe.legende.horsSeuil") }}
            </li>
            <li>{{ t("alarmesV2.courbe.legende.hachures") }}</li>
            <li v-if="getMarqueurs.length > 0">
                {{ t("alarmesV2.courbe.legende.evenement") }}
            </li>
        </ul>
        <figcaption
            v-if="getPoints.length > 0"
            class="text-sm text-surface-800"
            data-test="resume"
        >
            {{ getResume }}
        </figcaption>
    </figure>
</template>
